import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal } from "@angular/core";
import { TranslocoDirective, TranslocoService } from "@jsverse/transloco";
import {
  IButtonDirective,
  IFormFieldComponent,
  IInputDirective,
  ILabelComponent,
  IStepperComponent,
  ModalService,
  ToasterService,
} from "@front/m19-ui";
import {
  DspAdvertiserAudiencesCreate,
  DspAudienceCreateResponse,
  DspProductAudienceType,
  StrategyDspAudiencePreset,
} from "@front/m19-api-client";
import { Utils } from "@front/m19-utils";
import { ReactiveFormsModule } from "@angular/forms";
import { AccountSelectionService, AudiencesService } from "@front/m19-services";
import { switchMap, take } from "rxjs";
import {
  DspAudienceCreateResultModalData,
  DspCreateAudiencesResultModalComponent,
} from "@m19-board/dsp/dsp-create-audience-page/dsp-create-audiences-result/dsp-create-audiences-result-modal.component";
import {
  DspCreateAudienceProductsModel,
  DspCreateAudienceProductsStepComponent,
} from "@m19-board/dsp/dsp-create-audience-page/steps/dsp-create-audience-products-step.component";
import {
  DspCreateAudienceRulesModel,
  DspCreateAudienceRulesPreview,
  DspCreateAudienceRulesStepComponent,
} from "@m19-board/dsp/dsp-create-audience-page/steps/dsp-create-audience-rules-step.component";
import { ActivatedRoute, Router } from "@angular/router";
import { AjaxResponse } from "rxjs/ajax";
import {
  FieldContext,
  form,
  FormField,
  maxLength,
  minLength,
  required,
  validate,
  ValidationResult,
} from "@angular/forms/signals";
import { FormNavigationService, IFormStep } from "@m19-board/shared/form-navigation/form-navigation.service";
import { FormShellComponent } from "@m19-board/shared/form-shell/form-shell.component";

function validateAudienceConfigs(context: FieldContext<Record<DspProductAudienceType, number[]>>): ValidationResult {
  const configs = context.value();
  const hasAtLeastOneLookback = Object.values(configs).some((lookbacks) => lookbacks.length > 0);
  return hasAtLeastOneLookback ? null : { kind: "required" };
}

interface CreateDspAudienceForm {
  name: { audienceName: string };
  products: DspCreateAudienceProductsModel;
  rules: DspCreateAudienceRulesModel;
}

@Component({
  selector: "app-dsp-create-audience-page",
  providers: [FormNavigationService],
  imports: [
    TranslocoDirective,
    IButtonDirective,
    IFormFieldComponent,
    ILabelComponent,
    IInputDirective,
    IStepperComponent,
    ReactiveFormsModule,
    FormField,
    FormShellComponent,
    DspCreateAudienceProductsStepComponent,
    DspCreateAudienceRulesStepComponent,
  ],
  templateUrl: "./dsp-create-audience-page.component.html",
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class DspCreateAudiencePageComponent implements OnInit {
  private readonly translocoService = inject(TranslocoService);
  private readonly audiencesService = inject(AudiencesService);
  private readonly accountSelectionService = inject(AccountSelectionService);
  private readonly modalService = inject(ModalService);
  private readonly toasterService = inject(ToasterService);
  private readonly router = inject(Router);
  private readonly activatedRoute = inject(ActivatedRoute);
  private readonly formNavigationService = inject(FormNavigationService<CreateDspAudienceForm>);

  audienceCreationModel = signal<CreateDspAudienceForm>({
    name: { audienceName: "" },
    products: { asins: [] },
    rules: {
      audienceConfigs: {
        [DspProductAudienceType.VIEWS]: [],
        [DspProductAudienceType.PURCHASES]: [],
        [DspProductAudienceType.SEARCH]: [],
        [DspProductAudienceType.SIMS]: [],
      },
    },
  });

  audiencesCreationForm = form(
    this.audienceCreationModel,
    (schema) => {
      maxLength(schema.name.audienceName, 100);
      required(schema.name.audienceName);
      required(schema.products.asins);
      minLength(schema.products.asins, 1);
      required(schema.rules.audienceConfigs);
      validate(schema.rules.audienceConfigs, validateAudienceConfigs);
    },
    {
      submission: {
        action: async () => this.createAudiences(),
      },
    },
  );

  isCreatingAudiences = signal<boolean>(false);

  readonly currentStepId = this.formNavigationService.currentStepId;
  readonly formSteps = this.formNavigationService.formSteps;
  readonly isFirstStep = this.formNavigationService.isFirstStep;
  readonly isLastStep = this.formNavigationService.isLastStep;
  readonly canGoNext = this.formNavigationService.canGoNext;

  audiencesCreationConfig = computed<DspAdvertiserAudiencesCreate>(() => ({
    asins: this.audiencesCreationForm.products.asins().value(),
    baseName: this.audiencesCreationForm.name.audienceName().value(),
    audiencePreset: StrategyDspAudiencePreset.MANUAL,
    audiences: Utils.entriesFromObject(this.audiencesCreationForm.rules.audienceConfigs().value()).flatMap(
      ([type, lookbacks]) =>
        lookbacks.map((lookback) => ({
          audienceType: type,
          lookback,
        })),
    ),
  }));

  rulesPreview = computed<DspCreateAudienceRulesPreview>(() => ({
    baseName: this.audiencesCreationConfig().baseName,
    audiences: this.audiencesCreationConfig().audiences ?? [],
  }));

  ngOnInit(): void {
    this.formNavigationService.configure({
      form: this.audiencesCreationForm,
      blocked: this.isCreatingAudiences,
      steps: [
        { stepId: "name", label: this.translocoService.translate("common.name"), size: "sm" },
        { stepId: "products", label: this.translocoService.translate("common.products"), size: "md" },
        { stepId: "rules", label: this.translocoService.translate("common.rules"), size: "full" },
      ],
    });
  }

  goToStep(stepId: IFormStep<CreateDspAudienceForm>) {
    this.formNavigationService.goToStep(stepId);
  }

  cancel() {
    this.navigateToAudiencesPage();
  }

  back() {
    this.formNavigationService.back();
  }

  next() {
    this.formNavigationService.next();
  }

  createAudiences() {
    if (this.audiencesCreationForm().invalid()) {
      return;
    }

    this.isCreatingAudiences.set(true);
    this.accountSelectionService.singleAccountMarketplaceSelection$
      .pipe(
        take(1),
        switchMap((am) => this.audiencesService.createDspProductAudience(am, this.audiencesCreationConfig())),
      )
      .subscribe({
        next: () => {
          this.toasterService.success(
            this.translocoService.translate("dsp-create-audiences-page.audiences_successfully_created"),
          );
          this.navigateToAudiencesPage();
        },
        error: (audiencesCreateResponse: AjaxResponse<DspAudienceCreateResponse>) => {
          this.openRecapModal(audiencesCreateResponse.response);
        },
      });
  }

  private openRecapModal(audiencesCreateResult: DspAudienceCreateResponse | undefined) {
    this.modalService
      .openModal<DspAudienceCreateResultModalData | undefined, void>(DspCreateAudiencesResultModalComponent, {
        size: "md",
        modalTitle: this.translocoService.translate("dsp-create-audiences-result.audiences_creation"),
        data: {
          audiencesCreateResult,
        },
        closeMode: "none",
      })
      .afterClosed()
      .subscribe(() => this.navigateToAudiencesPage());
  }

  private navigateToAudiencesPage() {
    this.router.navigate([".."], { relativeTo: this.activatedRoute });
  }
}
